import {
  clientToContentPoint,
  LayoutAssetSceneViewport,
  type PdfRotation,
  type SvgLayoutDimensions,
} from '@ironview/interactive-viewer';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent,
} from 'react';

import { cn } from '@/lib/utils';

import { findContainingAreaTag, groupSafetyIssuesByPin, safetyPinKey } from './geometry';
import type { SafetyIssue, SafetyLayout, SafetyPinDraft } from './types';

const LONG_PRESS_MS = 650;
const MOVE_TOLERANCE_PX = 12;
const PIN_DRAG_THRESHOLD_PX = 5;
const isSafetyPinTarget = (target: EventTarget | null) =>
  target instanceof Element && Boolean(target.closest('[data-safety-pin]'));

const polygonCentroid = (points: number[][]) => {
  if (points.length === 0) return { x: 0, y: 0 };
  const total = points.reduce(
    (sum, point) => ({ x: sum.x + Number(point[0]), y: sum.y + Number(point[1]) }),
    { x: 0, y: 0 }
  );
  return { x: total.x / points.length, y: total.y / points.length };
};

const pdfRotation = (value: number | null): PdfRotation =>
  value === 90 || value === 180 || value === 270 ? value : 0;

interface SafetyLayoutCanvasProps {
  layout: SafetyLayout;
  issues: SafetyIssue[];
  issuesForOwnership?: SafetyIssue[];
  currentUserId: number | null;
  selectedIssueId?: string | null;
  onDropPin: (pin: SafetyPinDraft) => void;
  onMoveIssues: (move: { issueIds: string[]; x: number; y: number }) => Promise<void>;
  onSelectIssues: (issues: SafetyIssue[]) => void;
}

interface PinDragCandidate {
  pointerId: number;
  originKey: string;
  issueIds: string[];
  startClientX: number;
  startClientY: number;
  moved: boolean;
}

interface PinDragPreview {
  originKey: string;
  issueIds: string[];
  x: number;
  y: number;
}

export const SafetyLayoutCanvas = ({
  layout,
  issues,
  issuesForOwnership = issues,
  currentUserId,
  selectedIssueId,
  onDropPin,
  onMoveIssues,
  onSelectIssues,
}: SafetyLayoutCanvasProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const groupRef = useRef<SVGGElement>(null);
  const longPressRef = useRef<number | null>(null);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const pinDragRef = useRef<PinDragCandidate | null>(null);
  const latestDragPointRef = useRef<{ x: number; y: number } | null>(null);
  const suppressClickKeyRef = useRef<string | null>(null);
  const [dimensions, setDimensions] = useState<SvgLayoutDimensions>({ width: 1200, height: 800 });
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isCoarsePointer, setIsCoarsePointer] = useState(false);
  const [dragPreview, setDragPreview] = useState<PinDragPreview | null>(null);
  const isPdf = Boolean(layout.pdf_file_url);
  const issueGroups = useMemo(() => groupSafetyIssuesByPin(issues), [issues]);

  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    if (!layout.image_url) return;
    const image = new Image();
    image.onload = () => {
      if (image.naturalWidth > 0 && image.naturalHeight > 0) {
        setDimensions({ width: image.naturalWidth, height: image.naturalHeight });
      }
    };
    image.src = layout.image_url;
  }, [layout.id, layout.image_url]);

  useEffect(
    () => () => {
      if (longPressRef.current !== null) window.clearTimeout(longPressRef.current);
    },
    []
  );

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mediaQuery = window.matchMedia('(pointer: coarse)');
    const updatePointerType = () => setIsCoarsePointer(mediaQuery.matches);
    updatePointerType();
    mediaQuery.addEventListener('change', updatePointerType);
    return () => mediaQuery.removeEventListener('change', updatePointerType);
  }, []);

  const normalizedPointAt = useCallback(
    (clientX: number, clientY: number) => {
      const svg = svgRef.current;
      if (!svg || dimensions.width <= 0 || dimensions.height <= 0) return null;
      const point = clientToContentPoint(svg, clientX, clientY, {
        isPdfLayout: isPdf,
        position,
        scale,
      });
      if (!point) return null;
      if (point.x < 0 || point.y < 0 || point.x > dimensions.width || point.y > dimensions.height) {
        return null;
      }
      return {
        point,
        x: Math.min(1, Math.max(0, point.x / dimensions.width)),
        y: Math.min(1, Math.max(0, point.y / dimensions.height)),
      };
    },
    [dimensions.height, dimensions.width, isPdf, position, scale]
  );

  const placeAt = useCallback(
    (clientX: number, clientY: number) => {
      const normalized = normalizedPointAt(clientX, clientY);
      if (!normalized) return;
      const areaTag = findContainingAreaTag(layout.area_tags, normalized.point);
      onDropPin({
        layoutId: layout.id,
        x: normalized.x,
        y: normalized.y,
        locationId: areaTag?.id,
        locationName: areaTag?.name,
      });
    },
    [layout.area_tags, layout.id, normalizedPointAt, onDropPin]
  );

  const cancelLongPress = () => {
    if (longPressRef.current !== null) window.clearTimeout(longPressRef.current);
    longPressRef.current = null;
    pointerStartRef.current = null;
  };

  const scene = (sceneDimensions: SvgLayoutDimensions) => ({
    beforeAreaTags: (
      <>
        {layout.area_tags.map((areaTag) => {
          const points = areaTag.coordinates ?? [];
          if (points.length < 3) return null;
          const label = polygonCentroid(points);
          return (
            <g key={areaTag.id} className="pointer-events-none">
              <polygon
                points={points.map(([x, y]) => `${x},${y}`).join(' ')}
                fill="hsl(var(--primary) / 0.08)"
                stroke="hsl(var(--primary) / 0.34)"
                strokeWidth={2}
              />
              <text
                x={label.x}
                y={label.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="hsl(var(--foreground) / 0.75)"
                fontSize={Math.max(14, sceneDimensions.width / 80)}
                fontWeight={650}
              >
                {areaTag.name}
              </text>
            </g>
          );
        })}
      </>
    ),
    afterMarkers: (
      <>
        {issueGroups.map((pinIssues) => {
          const issue = pinIssues[0];
          if (issue.position_x == null || issue.position_y == null) return null;
          const originKey = safetyPinKey(issue.position_x, issue.position_y);
          const x = dragPreview?.originKey === originKey ? dragPreview.x : issue.position_x;
          const y = dragPreview?.originKey === originKey ? dragPreview.y : issue.position_y;
          const selected = pinIssues.some(({ id }) => id === selectedIssueId);
          const resolved = pinIssues.every(({ status }) => status === 'RESOLVED');
          const ownedIssueIds = issuesForOwnership
            .filter(
              ({ created_by, position_x, position_y }) =>
                created_by?.id === currentUserId &&
                position_x != null &&
                position_y != null &&
                safetyPinKey(position_x, position_y) === originKey
            )
            .map(({ id }) => id);
          const canMove = pinIssues.some(({ created_by }) => created_by?.id === currentUserId);
          const isDragging = Boolean(
            dragPreview && pinIssues.some(({ id }) => dragPreview.issueIds.includes(id))
          );
          const size = Math.max(15, sceneDimensions.width / 90);
          const markerColor = resolved ? 'hsl(var(--muted-foreground))' : 'hsl(var(--destructive))';
          const groupKey = `${originKey}:${pinIssues.map(({ id }) => id).join(',')}`;
          return (
            <g
              key={groupKey}
              data-safety-pin
              role="button"
              tabIndex={0}
              aria-label={`${pinIssues.length} safety ${pinIssues.length === 1 ? 'issue' : 'issues'} at this pin`}
              aria-grabbed={isDragging}
              transform={`translate(${x * sceneDimensions.width} ${y * sceneDimensions.height})`}
              style={{
                cursor: isDragging ? 'grabbing' : canMove ? 'grab' : 'pointer',
                pointerEvents: 'auto',
                touchAction: canMove ? 'none' : undefined,
              }}
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
              onPointerDown={(event) => {
                event.stopPropagation();
                if (!canMove || (event.pointerType === 'mouse' && event.button > 0)) return;
                event.preventDefault();
                pinDragRef.current = {
                  pointerId: event.pointerId,
                  originKey,
                  issueIds: ownedIssueIds,
                  startClientX: event.clientX,
                  startClientY: event.clientY,
                  moved: false,
                };
                latestDragPointRef.current = null;
                try {
                  event.currentTarget.setPointerCapture(event.pointerId);
                } catch {
                  // Pointer capture is unavailable in some embedded browsers.
                }
              }}
              onPointerMove={(event) => {
                const candidate = pinDragRef.current;
                if (!candidate || candidate.pointerId !== event.pointerId) return;
                event.preventDefault();
                event.stopPropagation();
                if (
                  !candidate.moved &&
                  Math.hypot(
                    event.clientX - candidate.startClientX,
                    event.clientY - candidate.startClientY
                  ) < PIN_DRAG_THRESHOLD_PX
                ) {
                  return;
                }
                const normalized = normalizedPointAt(event.clientX, event.clientY);
                if (!normalized) return;
                candidate.moved = true;
                latestDragPointRef.current = { x: normalized.x, y: normalized.y };
                setDragPreview({
                  originKey: candidate.originKey,
                  issueIds: candidate.issueIds,
                  x: normalized.x,
                  y: normalized.y,
                });
              }}
              onPointerUp={(event) => {
                const candidate = pinDragRef.current;
                if (!candidate || candidate.pointerId !== event.pointerId) return;
                event.stopPropagation();
                pinDragRef.current = null;
                try {
                  event.currentTarget.releasePointerCapture(event.pointerId);
                } catch {
                  // Pointer capture may already have been released by the browser.
                }
                const point = latestDragPointRef.current;
                latestDragPointRef.current = null;
                if (!candidate.moved || !point) return;
                suppressClickKeyRef.current = candidate.originKey;
                setDragPreview(null);
                window.setTimeout(() => {
                  if (suppressClickKeyRef.current === candidate.originKey) {
                    suppressClickKeyRef.current = null;
                  }
                }, 0);
                void onMoveIssues({
                  issueIds: candidate.issueIds,
                  x: point.x,
                  y: point.y,
                }).catch(() => undefined);
              }}
              onPointerCancel={(event) => {
                if (pinDragRef.current?.pointerId !== event.pointerId) return;
                pinDragRef.current = null;
                latestDragPointRef.current = null;
                setDragPreview(null);
              }}
              onClick={(event) => {
                event.stopPropagation();
                if (suppressClickKeyRef.current === originKey) {
                  suppressClickKeyRef.current = null;
                  return;
                }
                onSelectIssues(pinIssues);
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                onSelectIssues(pinIssues);
              }}
            >
              <svg
                x={-size * 1.5}
                y={-size * 2.75}
                width={size * 3}
                height={size * 3}
                viewBox="0 0 24 24"
                overflow="visible"
                aria-hidden="true"
              >
                <rect
                  x={0}
                  y={0}
                  width={24}
                  height={24}
                  fill="transparent"
                  style={{ pointerEvents: 'all' }}
                />
                {selected && (
                  <path
                    d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z"
                    fill="none"
                    stroke="hsl(var(--primary))"
                    strokeWidth={5}
                    strokeLinejoin="round"
                    opacity={0.55}
                  />
                )}
                <path
                  d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z"
                  fill={markerColor}
                  stroke="hsl(var(--background))"
                  strokeWidth={2}
                  strokeLinejoin="round"
                />
                <circle cx={12} cy={10} r={4.25} fill="hsl(var(--background) / 0.92)" />
                <text
                  x={12}
                  y={10.4}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill={markerColor}
                  fontSize={7.5}
                  fontWeight={900}
                >
                  !
                </text>
                {pinIssues.length > 1 && (
                  <g>
                    <circle
                      cx={19}
                      cy={4}
                      r={4.5}
                      fill="hsl(var(--primary))"
                      stroke="hsl(var(--background))"
                      strokeWidth={1.5}
                    />
                    <text
                      x={19}
                      y={4.25}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fill="hsl(var(--primary-foreground))"
                      fontSize={5.5}
                      fontWeight={800}
                    >
                      {pinIssues.length}
                    </text>
                  </g>
                )}
              </svg>
            </g>
          );
        })}
      </>
    ),
  });

  if (!layout.image_url && !layout.pdf_file_url) {
    return (
      <div className="grid h-full place-items-center rounded-[1.375rem] border border-dashed border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
        This layout does not have an image or PDF yet.
      </div>
    );
  }

  const pointerHandlers = {
    onDoubleClickCapture: (event: MouseEvent<HTMLDivElement>) => {
      if (isSafetyPinTarget(event.target)) return;
      event.preventDefault();
      placeAt(event.clientX, event.clientY);
    },
    onPointerDownCapture: (event: PointerEvent<HTMLDivElement>) => {
      if (event.pointerType !== 'touch' || isSafetyPinTarget(event.target)) return;
      pointerStartRef.current = { x: event.clientX, y: event.clientY };
      longPressRef.current = window.setTimeout(() => {
        placeAt(event.clientX, event.clientY);
        cancelLongPress();
      }, LONG_PRESS_MS);
    },
    onPointerMoveCapture: (event: PointerEvent<HTMLDivElement>) => {
      const start = pointerStartRef.current;
      if (!start) return;
      if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > MOVE_TOLERANCE_PX) {
        cancelLongPress();
      }
    },
    onPointerUpCapture: cancelLongPress,
    onPointerCancelCapture: cancelLongPress,
  };

  return (
    <div
      {...pointerHandlers}
      className={cn(
        'relative h-full min-h-[420px] overflow-hidden rounded-[1.375rem] border border-border bg-card shadow-slate',
        'select-none [&_.iv-layout-viewport__stage]:h-full'
      )}
    >
      <div className="pointer-events-none absolute left-4 top-4 z-20 rounded-full border border-border bg-background/85 px-3 py-1.5 text-xs font-semibold text-muted-foreground backdrop-blur">
        {isCoarsePointer ? 'Press and hold to mark an issue' : 'Double-click to mark an issue'}
      </div>
      {isPdf && layout.pdf_file_url ? (
        <LayoutAssetSceneViewport
          asset={{
            kind: 'pdf',
            url: layout.pdf_file_url,
            pageNumber: layout.pdf_page_number ?? 1,
            rotation: pdfRotation(layout.pdf_rotation),
            cropRegion: layout.pdf_crop_region,
            flippedHorizontal: layout.flipped_horizontal,
            flippedVertical: layout.flipped_vertical,
          }}
          position={position}
          scale={scale}
          pdfViewport={{
            onScaleChange: setScale,
            onPositionChange: setPosition,
            onFitComplete: (nextScale, nextPosition) => {
              setScale(nextScale);
              setPosition(nextPosition);
            },
            onDimensionsLoad: setDimensions,
            loadingMessage: 'Loading safety layout…',
          }}
          pdfOverlay={{ svgRef, groupRef }}
          scene={scene}
        />
      ) : (
        <LayoutAssetSceneViewport
          asset={{
            kind: 'image',
            url: layout.image_url ?? '',
            flippedHorizontal: layout.flipped_horizontal,
            flippedVertical: layout.flipped_vertical,
          }}
          position={position}
          scale={scale}
          imageViewport={{
            svgRef,
            groupRef,
            dimensions,
            cursor: 'crosshair',
          }}
          scene={scene}
        />
      )}
    </div>
  );
};
